// @flow

import * as React from 'react';
import {useEffect, useState} from 'react';
import {IntlProvider} from 'react-intl';
import {useAccount} from 'docs/data';
import type {Locale} from 'docs/data';
import {DEFAULT_LOCALE_CODE} from 'docs/util';
import {loadMessagesForLocale} from './loadMessagesForLocale';

type LocalizationProviderProps = {
  children?: React.Node,
  locale: Locale,
  messages: any,
};

export const LocalizationProvider = (props: LocalizationProviderProps) => {
  const {account} = useAccount();
  const [localizedMessages, setLocalizedMessages] = useState(props.messages);

  useEffect(() => {
    (async () => {
      // The messages for the user's initial locale are loaded on the server and
      // passed to the React app via the INITIAL_STATE blob. However, if the
      // user changes their locale, we need to load the JS bundle that contains
      // the messages for the new locale.
      if (account.locale.code !== props.locale.code) {
        const newMessages = await loadMessagesForLocale(account.locale);
        setLocalizedMessages(newMessages);
      }
    })();
  }, [account.locale, props.locale.code]);

  // Only use the messages stored in state if the user has changed them. This lets
  // us avoid an error message if the user toggles back to the originally-selected
  // language.
  //
  // TODO (nkohari): Handling the language-reload logic during the setLocale action
  // of the AccountReducer might solve this.
  const messages =
    account.locale.code === props.locale.code
      ? props.messages
      : localizedMessages;

  return (
    <IntlProvider
      locale={account.locale.code}
      messages={messages}
      // Errors about missing translations cause too much noise while developing so we turned them off.
      onError={() => {}}
      defaultLocale={DEFAULT_LOCALE_CODE}
    >
      {props.children}
    </IntlProvider>
  );
};
